import { Injectable } from "@angular/core";
import { createStore } from "@ngneat/elf";
import { selectAllEntities, setEntities, withEntities } from "@ngneat/elf-entities";
import { ApiService } from "./api.service";
import { filter, firstValueFrom, take } from "rxjs";
import { LocationStateQuery } from "../state/location-state.query";

const measurementStore = createStore({ name: "measurements" }, withEntities<UnitCategory>());

@Injectable({ providedIn: "root" })
export class MeasurementService {
	constructor(
		private apiService: ApiService,
		private locationStateQuery: LocationStateQuery,
	) {}
	public activate = async (): Promise<void> => {
		this.locationStateQuery.rideOpsKey$
			.pipe(
				filter((value): value is string => value != null),
				take(1),
			)
			.subscribe(() => {
				this.loadUnits();
			});
	};

	private loadUnits = async (): Promise<void> => {
		const unitCatalog = await this.apiService.unitCatalog();
		measurementStore.update(setEntities(unitCatalog.categories));
	};

	getUnitById = async (unitId: string): Promise<Unit | null> => {
		const categories = await firstValueFrom(measurementStore.pipe(selectAllEntities()));

		const [categoryId] = unitId.split("/");

		if (categoryId == null) {
			return null;
		}

		const category = categories.find((category) => category.id === categoryId);
		return category?.units.find((unit) => unit.id === unitId) ?? null;
	};
}

export interface UnitCatalog {
	categories: UnitCategory[];
}

export interface UnitCategory {
	id: string;
	type: UnitCategoryType;
	units: Unit[];
}

export interface Unit {
	id: string;
	symbol: string;
}

export enum UnitCategoryType {
	Currency = "Currency",
	Angles = "Angles",
	Area = "Area",
	ElectricCurrent = "ElectricCurrent",
	Energy = "Energy",
	Force = "Force",
	Length = "Length",
	Power = "Power",
	Pressure = "Pressure",
	Speed = "Speed",
	Temperature = "Temperature",
	Time = "Time",
	Torque = "Torque",
	Volume = "Volume",
	Mass = "Mass",
	Illuminance = "Illuminance",
	Luminance = "Luminance",
	Frequency = "Frequency",
	RotationalFrequency = "RotationalFrequency",
	Density = "Density",
	FlowMass = "FlowMass",
	FlowVolume = "FlowVolume",
	ViscosityDynamic = "ViscosityDynamic",
	ViscosityKinematic = "ViscosityKinematic",
	Acceleration = "Acceleration",
	Dimensionless = "Dimensionless",
}
